import API_BASE_URL from '../config/apiConfig';
import { apiClient } from './authService';

/**
 * Get KYC verifications by status with pagination
 * @param {string} status - Status filter: 'pending', 'verified', 'rejected', or 'all'
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<Object>} KYC verifications response
 */
export const getKYCVerifications = async (status = 'pending', page = 0, size = 20) => {
  try {
    let url = '';
    
    // Map status to endpoint
    // For pending, use the specific endpoint that works
    if (status === 'pending') {
      url = `${API_BASE_URL}/api/admin/kyc/pending?page=${page}&size=${size}`;
    } else {
      // For verified and rejected, use the base endpoint with status as query parameter
      // Map status values to match API expectations (uppercase)
      const statusMap = {
        'verified': 'APPROVED',  // Backend uses APPROVED instead of VERIFIED
        'rejected': 'REJECTED'
      };
      
      const statusParam = statusMap[status] || 'PENDING';
      // API expects status as a List<KycStatus>
      // Spring Boot accepts repeated query parameters for List types
      // We'll send status as a repeated parameter which Spring Boot will convert to a List
      const params = new URLSearchParams();
      // Append status parameter - Spring Boot will treat repeated params as a List
      params.append('status', statusParam);
      params.append('page', page.toString());
      params.append('size', size.toString());
      params.append('sort', 'createdAt,desc');
      url = `${API_BASE_URL}/api/admin/kyc?${params.toString()}`;
    }
    
    const response = await apiClient(
      url,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch KYC verifications';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get KYC verifications error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Approve a KYC verification
 * @param {string} kycId - KYC verification ID
 * @returns {Promise<Object>} Approval response
 */
export const approveKYC = async (kycId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/kyc/${kycId}/approve`,
      {
        method: 'POST',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to approve KYC verification';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Check if response status is success (even if HTTP status is ok)
    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to approve KYC verification';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Approve KYC error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Reject a KYC verification
 * @param {string} kycId - KYC verification ID
 * @param {string} reason - Rejection reason
 * @param {string} reasonCode - Rejection reason code (optional)
 * @returns {Promise<Object>} Rejection response
 */
export const rejectKYC = async (kycId, reason, reasonCode = null) => {
  try {
    const payload = {
      reason: reason,
    };
    
    if (reasonCode) {
      payload.reasonCode = reasonCode;
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/admin/kyc/${kycId}/reject`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to reject KYC verification';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Check if response status is success (even if HTTP status is ok)
    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to reject KYC verification';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Reject KYC error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get KYC verification by ID
 * @param {string} kycId - KYC verification ID
 * @returns {Promise<Object>} KYC verification response
 */
export const getKYCById = async (kycId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/kyc/${kycId}`,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch KYC verification';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get KYC by ID error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get KYB verifications by status with pagination
 * @param {string} status - Status filter: 'pending', 'verified', 'rejected', or 'all'
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<Object>} KYB verifications response
 */
export const uploadSystemAgreementFile = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'system-agreement');

    const response = await apiClient(
      `${API_BASE_URL}/api/files/upload`,
      {
        method: 'POST',
        body: formData,
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to upload agreement file';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Upload system agreement file error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const createSystemAgreement = async (agreementData) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/system-agreements`,
      {
        method: 'POST',
        body: JSON.stringify(agreementData),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to create system agreement';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Create system agreement error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const getSystemAgreements = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/system-agreements`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to load system agreements';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get system agreements error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const getActiveSystemAgreement = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/system-agreements/active`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to load the active agreement';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get active system agreement error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const getSystemAgreementById = async (agreementId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/system-agreements/${agreementId}`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to load system agreement';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get system agreement by ID error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const deleteSystemAgreement = async (agreementId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/system-agreements/${agreementId}`,
      {
        method: 'DELETE',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to delete agreement';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Delete system agreement error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const getKYBVerifications = async (status = 'pending', page = 0, size = 20) => {
  try {
    let url = '';
    
    // Map status to endpoint
    // For pending, use the specific endpoint
    if (status === 'pending') {
      url = `${API_BASE_URL}/api/admin/kyb/pending?page=${page}&size=${size}`;
    } else {
      // For verified and rejected, use the base endpoint with status as query parameter
      // Map status values to match API expectations (uppercase)
      const statusMap = {
        'verified': 'APPROVED',  // Backend uses APPROVED instead of VERIFIED
        'rejected': 'REJECTED'
      };
      
      const statusParam = statusMap[status] || 'PENDING_REVIEW';
      // API expects status as a List<KybStatus>
      // Spring Boot accepts repeated query parameters for List types
      const params = new URLSearchParams();
      params.append('status', statusParam);
      params.append('page', page.toString());
      params.append('size', size.toString());
      params.append('sort', 'createdAt,desc');
      url = `${API_BASE_URL}/api/admin/kyb?${params.toString()}`;
    }
    
    const response = await apiClient(
      url,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch KYB verifications';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get KYB verifications error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Approve a KYB verification
 * @param {string} kybId - KYB verification ID
 * @returns {Promise<Object>} Approval response
 */
export const approveKYB = async (kybId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/kyb/${kybId}/approve`,
      {
        method: 'POST',
      },
      true // Requires authentication
    );

    const data = await response.json();

    // Check response status - API returns status: "error" even if HTTP status is 200
    if (data.status === 'error') {
      const errorMessage = data.message || 'Failed to approve KYB verification';
      const error = new Error(errorMessage);
      error.response = data;
      error.isBusinessError = true; // Flag to indicate this is a business logic error, not HTTP error
      throw error;
    }

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to approve KYB verification';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Check if response status is success (even if HTTP status is ok)
    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to approve KYB verification';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Approve KYB error:', error);
    if (!error.response && !error.isBusinessError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Reject a KYB verification
 * @param {string} kybId - KYB verification ID
 * @param {string} reason - Rejection reason
 * @param {string} reasonCode - Rejection reason code (optional)
 * @returns {Promise<Object>} Rejection response
 */
export const rejectKYB = async (kybId, reason, reasonCode = null) => {
  try {
    const payload = {
      reason: reason,
    };
    
    if (reasonCode) {
      payload.reasonCode = reasonCode;
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/admin/kyb/${kybId}/reject`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to reject KYB verification';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Check if response status is success (even if HTTP status is ok)
    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to reject KYB verification';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Reject KYB error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get KYB verification by ID
 * @param {string} kybId - KYB verification ID
 * @returns {Promise<Object>} KYB verification response
 */
export const getKYBById = async (kybId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/kyb/${kybId}`,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch KYB verification';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get KYB by ID error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get role requests by status with pagination
 * @param {string} status - Status filter: 'pending', 'approved', 'rejected', or 'all'
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<Object>} Role requests response
 */
export const getRoleRequests = async (status = 'all', page = 0, size = 20) => {
  try {
    let url = '';
    
    // Map status to endpoint
    if (status === 'all') {
      // For 'all', fetch without status filter
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());
      params.append('sort', 'requestedAt,desc');
      url = `${API_BASE_URL}/api/role-requests?${params.toString()}`;
    } else if (status === 'pending') {
      // For pending, use the specific endpoint
      url = `${API_BASE_URL}/api/role-requests/pending?page=${page}&size=${size}`;
    } else {
      // For approved and rejected, use the base endpoint with status as query parameter
      const params = new URLSearchParams();
      
      if (status === 'rejected') {
        // For rejected, send both REJECTED and REJECTED_RESUBMIT_ALLOWED statuses
        params.append('status', 'REJECTED');
        params.append('status', 'REJECTED_RESUBMIT_ALLOWED');
      } else if (status === 'approved') {
        params.append('status', 'APPROVED');
      } else {
        params.append('status', 'PENDING');
      }
      
      params.append('page', page.toString());
      params.append('size', size.toString());
      params.append('sort', 'requestedAt,desc');
      url = `${API_BASE_URL}/api/role-requests?${params.toString()}`;
    }
    
    const response = await apiClient(
      url,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch role requests';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get role requests error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get pending role requests (backward compatibility)
 * @returns {Promise<Object>} Pending role requests response
 */
export const getPendingRoleRequests = async () => {
  return getRoleRequests('pending', 0, 20);
};

/**
 * Get role request by ID
 * @param {string} roleRequestId - Role request ID
 * @returns {Promise<Object>} Role request response
 */
export const getRoleRequestById = async (roleRequestId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/role-requests/${roleRequestId}`,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch role request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get role request by ID error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Approve a role request
 * @param {string} roleRequestId - Role request ID
 * @returns {Promise<Object>} Approval response
 */
export const approveRoleRequest = async (roleRequestId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/role-requests/${roleRequestId}/approve`,
      {
        method: 'POST',
      },
      true // Requires authentication
    );

    const data = await response.json();

    // Check response status - API returns status: "error" even if HTTP status is 200
    if (data.status === 'error') {
      const errorMessage = data.message || 'Failed to approve role request';
      const error = new Error(errorMessage);
      error.response = data;
      error.isBusinessError = true; // Flag to indicate this is a business logic error, not HTTP error
      throw error;
    }

    if (!response.ok) {
      // Extract error message from various possible locations in the response
      const errorMessage = data.message || data.error || data.errorMessage || 'Failed to approve role request';
      const error = new Error(errorMessage);
      error.response = { data, status: response.status };
      error.status = response.status;
      throw error;
    }

    // Check if response status is success (even if HTTP status is ok)
    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to approve role request';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Approve role request error:', error);
    if (!error.response && !error.isBusinessError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Reject a role request
 * @param {string} roleRequestId - Role request ID
 * @param {string} reasonCode - Rejection reason code (required)
 * @param {string} reason - Rejection reason (optional)
 * @param {boolean} allowResubmit - Whether to allow resubmission (optional, default: false)
 * @param {string} resubmitDeadline - Resubmit deadline date in YYYY-MM-DD format (optional)
 * @returns {Promise<Object>} Rejection response
 */
export const rejectRoleRequest = async (roleRequestId, reasonCode, reason = null, allowResubmit = false, resubmitDeadline = null) => {
  try {
    const payload = {
      reasonCode: reasonCode,
    };
    
    if (reason) {
      payload.reason = reason;
    }
    
    if (allowResubmit) {
      payload.allowResubmit = true;
      if (resubmitDeadline) {
        payload.resubmitDeadline = resubmitDeadline;
      }
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/role-requests/${roleRequestId}/reject`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to reject role request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Check if response status is success (even if HTTP status is ok)
    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to reject role request';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Reject role request error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get all users with pagination
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @param {string} sort - Sort parameter (e.g., 'createdAt,desc')
 * @returns {Promise<Object>} Users response
 */
export const getAllUsers = async (page = 0, size = 20, sort = 'createdAt,desc') => {
  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('size', size.toString());
    params.append('sort', sort);
    
    const url = `${API_BASE_URL}/api/admin/users?${params.toString()}`;
    
    const response = await apiClient(
      url,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch users';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get all users error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get user by ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User response
 */
export const getUserById = async (userId) => {
  try {
    const url = `${API_BASE_URL}/api/admin/users/${userId}`;
    
    const response = await apiClient(
      url,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch user';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get user by ID error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Suspend a user
 * @param {string} userId - User ID
 * @param {Object} options - Suspend options
 * @param {string} options.reason - Required reason for suspension
 * @param {boolean} [options.allowUnsuspensionRequest=true] - Whether the user may request unsuspension
 * @returns {Promise<Object>} Suspend response
 */
export const suspendUser = async (userId, { reason, allowUnsuspensionRequest = true }) => {
  try {
    const url = `${API_BASE_URL}/api/admin/users/${userId}/suspend`;
    const payload = {
      reason,
      allowUnsuspensionRequest,
    };

    const response = await apiClient(
      url,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to suspend user';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Suspend user error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Unsuspend a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Unsuspend response
 */
export const unsuspendUser = async (userId) => {
  try {
    const url = `${API_BASE_URL}/api/admin/users/${userId}/unsuspend`;
    
    const response = await apiClient(
      url,
      {
        method: 'POST',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to unsuspend user';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Unsuspend user error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get pending unsuspension requests with pagination
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<Object>} Unsuspension requests response
 */
export const getPendingUnsuspensionRequests = async (page = 0, size = 20) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('size', size.toString());
    
    const url = `${API_BASE_URL}/api/admin/users/unsuspension-requests/pending?${params.toString()}`;
    
    const response = await apiClient(
      url,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch unsuspension requests';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get pending unsuspension requests error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Approve an unsuspension request
 * @param {string} unsuspensionRequestId - Unsuspension request ID
 * @returns {Promise<Object>} Approval response
 */
export const approveUnsuspensionRequest = async (unsuspensionRequestId) => {
  try {
    const url = `${API_BASE_URL}/api/admin/users/unsuspension-requests/${unsuspensionRequestId}/approve`;
    
    const response = await apiClient(
      url,
      {
        method: 'POST',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to approve unsuspension request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Approve unsuspension request error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Reject an unsuspension request
 * @param {string} unsuspensionRequestId - Unsuspension request ID
 * @returns {Promise<Object>} Rejection response
 */
export const rejectUnsuspensionRequest = async (unsuspensionRequestId) => {
  try {
    const url = `${API_BASE_URL}/api/admin/users/unsuspension-requests/${unsuspensionRequestId}/reject`;
    
    const response = await apiClient(
      url,
      {
        method: 'POST',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to reject unsuspension request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Reject unsuspension request error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Create a unit category
 * @param {string} name - Unit category name (e.g., "Residential", "Commercial")
 * @param {number} displayOrder - Display order for sorting
 * @returns {Promise<Object>} Create unit category response
 */
export const createUnitCategory = async (name, displayOrder) => {
  try {
    const url = `${API_BASE_URL}/api/admin/unit-categories`;
    
    const payload = {
      name: name,
      displayOrder: displayOrder
    };

    const response = await apiClient(
      url,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to create unit category';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Check if response status is success (even if HTTP status is ok)
    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to create unit category';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Create unit category error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get all unit categories
 * @returns {Promise<Object>} Unit categories response
 */
export const getUnitCategories = async () => {
  try {
    const url = `${API_BASE_URL}/api/unit-categories`;
    
    const response = await apiClient(
      url,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch unit categories';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get unit categories error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Update a unit category
 * @param {string} categoryId - Unit category ID
 * @param {string} name - Unit category name
 * @param {number} displayOrder - Display order for sorting
 * @returns {Promise<Object>} Update unit category response
 */
export const updateUnitCategory = async (categoryId, name, displayOrder) => {
  try {
    const id = String(categoryId || '').trim();
    if (!id) {
      throw new Error('Category ID is required');
    }

    const url = `${API_BASE_URL}/api/admin/unit-categories/${encodeURIComponent(id)}`;
    const payload = { name, displayOrder };

    const response = await apiClient(
      url,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to update unit category';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to update unit category';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Update unit category error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Delete a unit category
 * @param {string} categoryId - Unit category ID
 * @returns {Promise<Object>} Delete unit category response
 */
export const deleteUnitCategory = async (categoryId) => {
  try {
    const id = String(categoryId || '').trim();
    if (!id) {
      throw new Error('Category ID is required');
    }

    const url = `${API_BASE_URL}/api/admin/unit-categories/${encodeURIComponent(id)}`;
    const response = await apiClient(url, { method: 'DELETE' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to delete unit category';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to delete unit category';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Delete unit category error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Create a unit subcategory
 * @param {string} name - Unit subcategory name (e.g., "Rental", "BNB", "Shop", "Office")
 * @param {string} categoryId - Unit category ID
 * @param {number} displayOrder - Display order for sorting
 * @returns {Promise<Object>} Create unit subcategory response
 */
export const createUnitSubcategory = async (name, categoryId, displayOrder) => {
  try {
    const url = `${API_BASE_URL}/api/admin/unit-subcategories`;
    
    const payload = {
      name: name,
      categoryId: categoryId,
      displayOrder: displayOrder
    };

    const response = await apiClient(
      url,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to create unit subcategory';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Check if response status is success (even if HTTP status is ok)
    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to create unit subcategory';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Create unit subcategory error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Update a unit subcategory
 * @param {string} subcategoryId - Unit subcategory ID
 * @param {string} name - Unit subcategory name
 * @param {string} categoryId - Unit category ID
 * @param {number} displayOrder - Display order for sorting
 * @returns {Promise<Object>} Update unit subcategory response
 */
export const updateUnitSubcategory = async (subcategoryId, name, categoryId, displayOrder) => {
  try {
    const id = String(subcategoryId || '').trim();
    if (!id) {
      throw new Error('Subcategory ID is required');
    }

    const url = `${API_BASE_URL}/api/admin/unit-subcategories/${encodeURIComponent(id)}`;
    const payload = { name, categoryId, displayOrder };

    const response = await apiClient(
      url,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to update unit subcategory';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to update unit subcategory';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Update unit subcategory error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Delete a unit subcategory
 * @param {string} subcategoryId - Unit subcategory ID
 * @returns {Promise<Object>} Delete unit subcategory response
 */
export const deleteUnitSubcategory = async (subcategoryId) => {
  try {
    const id = String(subcategoryId || '').trim();
    if (!id) {
      throw new Error('Subcategory ID is required');
    }

    const url = `${API_BASE_URL}/api/admin/unit-subcategories/${encodeURIComponent(id)}`;
    const response = await apiClient(url, { method: 'DELETE' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to delete unit subcategory';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to delete unit subcategory';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Delete unit subcategory error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Create a unit type
 * @param {string} name - Unit type name (e.g., "1 Bedroom", "Studio", "Single Room")
 * @param {number} displayOrder - Display order for sorting
 * @returns {Promise<Object>} Create unit type response
 */
export const createUnitType = async (name, displayOrder) => {
  try {
    const url = `${API_BASE_URL}/api/admin/unit-types`;
    
    const payload = {
      name: name,
      displayOrder: displayOrder
    };

    const response = await apiClient(
      url,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to create unit type';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Check if response status is success (even if HTTP status is ok)
    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to create unit type';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Create unit type error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get all unit types
 * @returns {Promise<Object>} Unit types response
 */
export const getUnitTypes = async () => {
  try {
    const url = `${API_BASE_URL}/api/unit-types`;
    
    const response = await apiClient(
      url,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch unit types';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get unit types error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Update a unit type
 * @param {string} typeId - Unit type ID
 * @param {string} name - Unit type name
 * @param {number} displayOrder - Display order for sorting
 * @returns {Promise<Object>} Update unit type response
 */
export const updateUnitType = async (typeId, name, displayOrder) => {
  try {
    const id = String(typeId || '').trim();
    if (!id) {
      throw new Error('Unit type ID is required');
    }

    const url = `${API_BASE_URL}/api/admin/unit-types/${encodeURIComponent(id)}`;
    const payload = { name, displayOrder };

    const response = await apiClient(
      url,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to update unit type';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to update unit type';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Update unit type error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Delete a unit type
 * @param {string} typeId - Unit type ID
 * @returns {Promise<Object>} Delete unit type response
 */
export const deleteUnitType = async (typeId) => {
  try {
    const id = String(typeId || '').trim();
    if (!id) {
      throw new Error('Unit type ID is required');
    }

    const url = `${API_BASE_URL}/api/admin/unit-types/${encodeURIComponent(id)}`;
    const response = await apiClient(url, { method: 'DELETE' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to delete unit type';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to delete unit type';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Delete unit type error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get unit subcategories by category ID
 * @param {string} categoryId - Unit category ID
 * @returns {Promise<Object>} Unit subcategories response
 */
export const getUnitSubcategories = async (categoryId) => {
  try {
    const url = `${API_BASE_URL}/api/unit-subcategories?categoryId=${categoryId}`;

    const response = await apiClient(
      url,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch unit subcategories';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get unit subcategories error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/* ----------------------- System Settings ----------------------- */

/**
 * Get current system settings (subscriptionEnabled, facebookAutoPostEnabled).
 * @returns {Promise<Object>} System settings response
 */
export const getSystemSettings = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/settings`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch system settings';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get system settings error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Update system settings toggles. Pass undefined/null to leave a toggle unchanged.
 * @param {Object} options
 * @param {boolean} [options.subscriptionEnabled]
 * @param {boolean} [options.facebookAutoPostEnabled]
 * @returns {Promise<Object>} Updated system settings response
 */
export const updateSystemSettings = async ({ subscriptionEnabled, facebookAutoPostEnabled } = {}) => {
  try {
    const params = new URLSearchParams();
    if (subscriptionEnabled !== undefined && subscriptionEnabled !== null) {
      params.append('subscriptionEnabled', String(subscriptionEnabled));
    }
    if (facebookAutoPostEnabled !== undefined && facebookAutoPostEnabled !== null) {
      params.append('facebookAutoPostEnabled', String(facebookAutoPostEnabled));
    }

    const url = `${API_BASE_URL}/api/admin/settings${params.toString() ? `?${params.toString()}` : ''}`;

    const response = await apiClient(
      url,
      { method: 'PUT' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to update system settings';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to update system settings';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Update system settings error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/* ----------------------- Integration Configs ----------------------- */

/**
 * List integration configs (payment providers + facebook), optionally filtered.
 * @param {Object} [filters]
 * @param {('PAYMENT'|'FACEBOOK')} [filters.category]
 * @param {string} [filters.provider]
 * @returns {Promise<Object>} Integration configs response
 */
export const getIntegrationConfigs = async ({ category, provider } = {}) => {
  try {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (provider) params.append('provider', provider);

    const url = `${API_BASE_URL}/api/admin/integration-configs${params.toString() ? `?${params.toString()}` : ''}`;

    const response = await apiClient(
      url,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch integration configs';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get integration configs error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Create or update (upsert by category+provider) an integration config.
 * @param {Object} dto
 * @param {('PAYMENT'|'FACEBOOK')} dto.category
 * @param {string} dto.provider - e.g. MPESA, PAYSTACK, FACEBOOK_PAGE
 * @param {string} [dto.displayName]
 * @param {boolean} [dto.enabled]
 * @param {Object} [dto.credentials] - provider-specific key/value map
 * @returns {Promise<Object>} Saved integration config response
 */
export const upsertIntegrationConfig = async (dto) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/integration-configs`,
      {
        method: 'POST',
        body: JSON.stringify(dto),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to save integration config';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to save integration config';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Upsert integration config error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Delete an integration config by ID.
 * @param {string} configId - Integration config ID
 * @returns {Promise<Object>} Delete response
 */
export const deleteIntegrationConfig = async (configId) => {
  try {
    const id = String(configId || '').trim();
    if (!id) {
      throw new Error('Integration config ID is required');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/admin/integration-configs/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to delete integration config';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to delete integration config';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Delete integration config error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Post a property to Facebook (admin action)
 * Endpoint: POST /api/admin/properties/{propertyId}/post-to-facebook
 * @param {string} propertyId - Property ID
 * @returns {Promise<Object>} API response
 */
export const postPropertyToFacebook = async (propertyId) => {
  try {
    const id = String(propertyId || '').trim();
    if (!id) {
      throw new Error('Property ID is required');
    }

    const url = `${API_BASE_URL}/api/admin/properties/${encodeURIComponent(id)}/post-to-facebook`;

    const response = await apiClient(
      url,
      {
        method: 'POST',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to post property to Facebook';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Post property to Facebook error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/* =====================================================================
   Subscriptions (admin)
   ===================================================================== */

/**
 * List all subscriptions (optionally filtered by userId).
 * GET /api/admin/subscriptions?userId=&page=&size=
 * @param {{ userId?: string, page?: number, size?: number }} opts
 */
export const getAdminSubscriptions = async (opts = {}) => {
  try {
    const params = new URLSearchParams();
    if (opts.userId) params.append('userId', opts.userId);
    if (opts.page != null) params.append('page', String(opts.page));
    if (opts.size != null) params.append('size', String(opts.size));

    const response = await apiClient(
      `${API_BASE_URL}/api/admin/subscriptions?${params.toString()}`,
      { method: 'GET' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to fetch subscriptions'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/**
 * Grant a subscription to a user.
 * POST /api/admin/subscriptions  body: { userId, planId, startDate?, endDate? }
 * @param {{ userId: string, planId: string, startDate?: string, endDate?: string }} payload
 */
export const createAdminSubscription = async (payload) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/subscriptions`,
      { method: 'POST', body: JSON.stringify(payload) },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to create subscription'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/**
 * Cancel a subscription.
 * POST /api/admin/subscriptions/{subscriptionId}/cancel
 */
export const cancelAdminSubscription = async (subscriptionId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`,
      { method: 'POST' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to cancel subscription'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/* =====================================================================
   Audit logs
   ===================================================================== */

/**
 * List audit logs with filters.
 * GET /api/admin/audit-logs?adminUserId=&targetUserId=&action=&from=&to=&page=&size=
 */
export const getAuditLogs = async (opts = {}) => {
  try {
    const params = new URLSearchParams();
    if (opts.adminUserId) params.append('adminUserId', opts.adminUserId);
    if (opts.targetUserId) params.append('targetUserId', opts.targetUserId);
    if (opts.action) params.append('action', opts.action);
    if (opts.from) params.append('from', opts.from);
    if (opts.to) params.append('to', opts.to);
    if (opts.page != null) params.append('page', String(opts.page));
    if (opts.size != null) params.append('size', String(opts.size));

    const response = await apiClient(
      `${API_BASE_URL}/api/admin/audit-logs?${params.toString()}`,
      { method: 'GET' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to fetch audit logs'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/**
 * Get audit log stats (counts by action, recent activity).
 * GET /api/admin/audit-logs/stats
 */
export const getAuditLogStats = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/audit-logs/stats`,
      { method: 'GET' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to fetch audit log stats'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/* =====================================================================
   Bulk user actions & role management
   ===================================================================== */

/**
 * Perform a bulk action on multiple users.
 * POST /api/admin/users/bulk-action  body: { userIds[], action, reason, allowUnsuspensionRequest }
 * @param {{ userIds: string[], action: 'SUSPEND'|'UNSUSPEND'|'DELETE', reason?: string, allowUnsuspensionRequest?: boolean }} payload
 */
export const bulkUserAction = async (payload) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/users/bulk-action`,
      { method: 'POST', body: JSON.stringify(payload) },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Bulk action failed'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/**
 * Grant a role to a user (bypass approval flow).
 * POST /api/admin/users/{userId}/roles  body: { role }
 */
export const grantUserRole = async (userId, role) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}/roles`,
      { method: 'POST', body: JSON.stringify({ role }) },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to grant role'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/**
 * Revoke a role from a user.
 * DELETE /api/admin/users/{userId}/roles/{role}
 */
export const revokeUserRole = async (userId, role) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(role)}`,
      { method: 'DELETE' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to revoke role'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/* =====================================================================
   Admin properties — block/unblock/publish/unpublish + list
   ===================================================================== */

/**
 * List all properties with filters (admin).
 * GET /api/admin/properties?search=&ownerId=&agentId=&published=&isActive=&facebookPosted=&page=&size=
 */
export const getAdminProperties = async (opts = {}) => {
  try {
    const params = new URLSearchParams();
    if (opts.search) params.append('search', opts.search);
    if (opts.ownerId) params.append('ownerId', opts.ownerId);
    if (opts.agentId) params.append('agentId', opts.agentId);
    if (opts.published != null) params.append('published', String(opts.published));
    if (opts.isActive != null) params.append('isActive', String(opts.isActive));
    if (opts.facebookPosted != null) params.append('facebookPosted', String(opts.facebookPosted));
    if (opts.page != null) params.append('page', String(opts.page));
    if (opts.size != null) params.append('size', String(opts.size));

    const response = await apiClient(
      `${API_BASE_URL}/api/admin/properties?${params.toString()}`,
      { method: 'GET' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to fetch properties'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/**
 * List all tenancies (tenant assignments) across every property (admin).
 * GET /api/admin/properties/tenancies?status=&page=&size=
 * @param {{ status?: 'ACTIVE'|'ENDED', page?: number, size?: number }} opts
 */
export const getAdminTenancies = async (opts = {}) => {
  try {
    const params = new URLSearchParams();
    if (opts.status) params.append('status', opts.status);
    if (opts.page != null) params.append('page', String(opts.page));
    if (opts.size != null) params.append('size', String(opts.size));

    const response = await apiClient(
      `${API_BASE_URL}/api/admin/properties/tenancies?${params.toString()}`,
      { method: 'GET' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to fetch tenancies'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

const _propertyStateAction = async (propertyId, action) => {

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/properties/${encodeURIComponent(propertyId)}/${action}`,
      { method: 'PATCH' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || `Failed to ${action} property`), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

export const blockProperty = (propertyId) => _propertyStateAction(propertyId, 'block');
export const unblockProperty = (propertyId) => _propertyStateAction(propertyId, 'unblock');
export const publishProperty = (propertyId) => _propertyStateAction(propertyId, 'publish');
export const unpublishProperty = (propertyId) => _propertyStateAction(propertyId, 'unpublish');

/**
 * Get a single property (with units) for admin review.
 * GET /api/admin/properties/{propertyId}
 */
export const getAdminPropertyById = async (propertyId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/properties/${encodeURIComponent(propertyId)}`,
      { method: 'GET' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to fetch property'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

const _unitStateAction = async (propertyId, unitId, action) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/properties/${encodeURIComponent(propertyId)}/units/${encodeURIComponent(unitId)}/${action}`,
      { method: 'PATCH' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || `Failed to ${action} unit`), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

export const blockUnit = (propertyId, unitId) => _unitStateAction(propertyId, unitId, 'block');
export const unblockUnit = (propertyId, unitId) => _unitStateAction(propertyId, unitId, 'unblock');
export const publishUnit = (propertyId, unitId) => _unitStateAction(propertyId, unitId, 'publish');
export const unpublishUnit = (propertyId, unitId) => _unitStateAction(propertyId, unitId, 'unpublish');

/**
 * Admin: soft-delete a property (cascades to units, removes stored images).
 * DELETE /api/admin/properties/{propertyId}
 */
export const deleteAdminProperty = async (propertyId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/properties/${encodeURIComponent(propertyId)}`,
      { method: 'DELETE' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to delete property'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/**
 * Admin: delete a unit (soft delete, removes stored photos).
 * DELETE /api/admin/properties/{propertyId}/units/{unitId}
 */
export const deleteAdminUnit = async (propertyId, unitId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/properties/${encodeURIComponent(propertyId)}/units/${encodeURIComponent(unitId)}`,
      { method: 'DELETE' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to delete unit'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};


/* =====================================================================
   Notifications — broadcast & FCM diagnostics
   ===================================================================== */

/**
 * Broadcast a notification to all users or a specific role.
 * POST /api/admin/notifications/broadcast  body: { title, body, targetRole?, clickUrl? }
 */
export const broadcastNotification = async (payload) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/notifications/broadcast`,
      { method: 'POST', body: JSON.stringify(payload) },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Broadcast failed'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/**
 * Look up FCM tokens for a user by email.
 * GET /api/admin/notifications/test/fcm-tokens?email=
 */
export const lookupFcmTokens = async (email) => {
  try {
    const params = new URLSearchParams();
    if (email) params.append('email', email);
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/notifications/test/fcm-tokens?${params.toString()}`,
      { method: 'GET' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to lookup FCM tokens'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/**
 * Send a test FCM push to a device token.
 * POST /api/admin/notifications/test/fcm-send  body: { token, title?, message, imageUrl? }
 */
export const sendTestFcm = async (payload) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/admin/notifications/test/fcm-send`,
      { method: 'POST', body: JSON.stringify(payload) },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Test FCM send failed'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/* ─── DATA EXPORTS REPORTS ─── */
/**
 * Generic endpoint to fetch report data for Data Exports
 * @param {string} reportType - users, properties, tenancies, subscriptions, reports
 * @param {Object} filters - Key-value pair of filters
 */
export const getReportData = async (reportType, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'ALL') {
      params.append(key, value);
    }
  });
  const res = await apiClient(`${API_BASE_URL}/api/admin/reports/${reportType}?${params.toString()}`, { method: 'GET' }, true);
  const data = await res.json();
  return data?.data || data;
};